import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { RefreshCw, Plus, Pause, Play, Trash2, Calendar, Euro, Repeat, Loader2, Zap, ArrowLeft } from 'lucide-react';
import { recurringAPI, clientsAPI } from '../../services/api';
import { toast } from 'sonner';

const RecurringPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, paused: 0, mrr_estimate: 0 });
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [processing, setProcessing] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, item: null });
  const [deleting, setDeleting] = useState(false);
  
  const [form, setForm] = useState({
    client_id: '',
    client_name: '',
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 20 }],
    notes: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [itemsRes, statsRes, clientsRes] = await Promise.all([
        recurringAPI.getAll(),
        recurringAPI.getStats(),
        clientsAPI.getAll()
      ]);
      setItems(itemsRes.data);
      setStats(statsRes.data);
      setClients(clientsRes.data);
    } catch (e) {
      toast.error('Erreur chargement');
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!form.client_id || form.items[0].description === '') {
      toast.error('Remplissez tous les champs');
      return;
    }
    try {
      await recurringAPI.create(form);
      toast.success('Facture récurrente créée !');
      setShowModal(false);
      loadData();
    } catch (e) {
      toast.error('Erreur création');
    }
  };

  const handlePause = async (id) => {
    setProcessing(p => ({ ...p, [id]: true }));
    try {
      await recurringAPI.pause(id);
      toast.success('Mis en pause');
      loadData();
    } catch (e) { toast.error('Erreur'); }
    setProcessing(p => ({ ...p, [id]: false }));
  };

  const handleResume = async (id) => {
    setProcessing(p => ({ ...p, [id]: true }));
    try {
      await recurringAPI.resume(id);
      toast.success('Repris');
      loadData();
    } catch (e) { toast.error('Erreur'); }
    setProcessing(p => ({ ...p, [id]: false }));
  };

  const handleGenerate = async (id) => {
    setProcessing(p => ({ ...p, [`gen_${id}`]: true }));
    try {
      const res = await recurringAPI.generate(id);
      toast.success(`Facture ${res.data.invoice_number} générée !`);
      loadData();
    } catch (e) { toast.error('Erreur'); }
    setProcessing(p => ({ ...p, [`gen_${id}`]: false }));
  };

  const handleDelete = async (item) => {
    setDeleteConfirm({ open: true, item });
  };

  const confirmDelete = async () => {
    const item = deleteConfirm.item;
    if (!item) return;
    
    setDeleting(true);
    try {
      await recurringAPI.delete(item.id);
      toast.success('Supprimé');
      loadData();
      setDeleteConfirm({ open: false, item: null });
    } catch (e) { 
      toast.error('Erreur'); 
    } finally {
      setDeleting(false);
    }
  };

  const updateItem = (idx, field, value) => {
    const newItems = [...form.items];
    newItems[idx][field] = field === 'description' ? value : parseFloat(value) || 0;
    setForm({ ...form, items: newItems });
  };

  const selectClient = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setForm({ ...form, client_id: clientId, client_name: client?.name || '' });
  };

  const getStatusBadge = (status) => {
    if (status === 'active') return <Badge className="bg-emerald-100 text-emerald-700">Actif</Badge>;
    if (status === 'paused') return <Badge className="bg-amber-100 text-amber-700">En pause</Badge>;
    return <Badge className="bg-slate-100 text-slate-700">Terminé</Badge>;
  };

  const formatAmount = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/dashboard/smartfacture')}
        className="text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Retour
      </Button>

      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white/20"><Repeat className="h-8 w-8" /></div>
            <div>
              <h1 className="text-2xl font-bold">Factures Récurrentes</h1>
              <p className="text-emerald-100">Automatisez votre facturation mensuelle</p>
            </div>
          </div>
          <Button onClick={() => setShowModal(true)} className="bg-white text-emerald-700 hover:bg-emerald-50">
            <Plus className="h-4 w-4 mr-2" />Nouvelle
          </Button>
        </div>
        
        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="bg-white/10 rounded-xl p-4">
            <p className="text-sm text-emerald-100">Actives</p>
            <p className="text-3xl font-bold">{stats.active}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4">
            <p className="text-sm text-emerald-100">En pause</p>
            <p className="text-3xl font-bold">{stats.paused}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4">
            <p className="text-sm text-emerald-100">MRR estimé</p>
            <p className="text-3xl font-bold">{formatAmount(stats.mrr_estimate)}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4">
            <p className="text-sm text-emerald-100">ARR estimé</p>
            <p className="text-3xl font-bold">{formatAmount(stats.arr_estimate)}</p>
          </div>
        </div>
      </div>

      {/* Liste */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Vos factures récurrentes</span>
            <Button variant="outline" size="sm" onClick={loadData}><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-8">
              <Repeat className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Aucune facture récurrente</p>
              <Button className="mt-4" onClick={() => setShowModal(true)}><Plus className="h-4 w-4 mr-2" />Créer</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-4 border rounded-xl hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-emerald-100"><Repeat className="h-5 w-5 text-emerald-600" /></div>
                    <div>
                      <p className="font-medium">{item.client_name}</p>
                      <p className="text-sm text-slate-500">{item.frequency_label} • Prochaine: {item.next_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-lg">{formatAmount(item.total_ttc)}</p>
                      <p className="text-xs text-slate-500">{item.generated_count} générée(s)</p>
                    </div>
                    {getStatusBadge(item.status)}
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleGenerate(item.id)} disabled={processing[`gen_${item.id}`]}>
                        {processing[`gen_${item.id}`] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                      </Button>
                      {item.status === 'active' ? (
                        <Button variant="ghost" size="sm" onClick={() => handlePause(item.id)}><Pause className="h-4 w-4" /></Button>
                      ) : item.status === 'paused' ? (
                        <Button variant="ghost" size="sm" onClick={() => handleResume(item.id)}><Play className="h-4 w-4" /></Button>
                      ) : null}
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(item)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Création */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouvelle facture récurrente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={selectClient}>
                <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fréquence</Label>
                <Select value={form.frequency} onValueChange={v => setForm({ ...form, frequency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Hebdomadaire</SelectItem>
                    <SelectItem value="monthly">Mensuel</SelectItem>
                    <SelectItem value="quarterly">Trimestriel</SelectItem>
                    <SelectItem value="yearly">Annuel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date début</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Prestation</Label>
              <Input placeholder="Description" value={form.items[0].description} onChange={e => updateItem(0, 'description', e.target.value)} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Quantité</Label>
                <Input type="number" value={form.items[0].quantity} onChange={e => updateItem(0, 'quantity', e.target.value)} />
              </div>
              <div>
                <Label>Prix HT</Label>
                <Input type="number" value={form.items[0].unit_price} onChange={e => updateItem(0, 'unit_price', e.target.value)} />
              </div>
              <div>
                <Label>TVA %</Label>
                <Input type="number" value={form.items[0].tax_rate} onChange={e => updateItem(0, 'tax_rate', e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Notes (optionnel)</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button onClick={handleCreate} className="bg-emerald-600">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation de suppression */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open, item: open ? deleteConfirm.item : null })}
        title="Supprimer cette facture récurrente ?"
        description={`Supprimer la facture récurrente pour "${deleteConfirm.item?.client_name}" ? Les factures déjà générées ne seront pas affectées.`}
        confirmText="Supprimer"
        onConfirm={confirmDelete}
        loading={deleting}
        variant="destructive"
      />
    </div>
  );
};

export default RecurringPage;
